import { useId, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Card, EmptyState, Icon, Select } from '@mypoz/ui';

import {
  clampPackagePage,
  notificationKey,
  packagePageCount,
  parsePackagePage,
} from '@mypoz/shared';

import { NotificationRow } from '@/features/notifications/components/NotificationRow.js';
import { NotificationThreadPanel } from '@/features/notifications/components/NotificationThreadPanel.js';
import { useFormat } from '@/lib/format.js';
import { useNotifications } from '@/features/notifications/context.js';
import { NOTIFICATION_TYPES } from '@/features/notifications/catalog.js';
import { resolveNotificationLink } from '@/features/notifications/resolveLink.js';
import { usePackages } from '@/features/packages/index.js';
import { Pager } from '@/components/Pager.js';
import { buttonClass } from '@/lib/styles.js';

const ALL_TYPES = 'ALL';
const STATUS = { unread: 'UNREAD', all: 'ALL' } as const;
type StatusFilter = (typeof STATUS)[keyof typeof STATUS];

/**
 * Rows per page. Client-side only — the whole account is already in memory by the time this page
 * renders (see `NotificationsProvider`), so this pages the render, not the fetch. `get_msgs` does
 * take `page`/`limit`, but it sorts by type rather than date, so a vendor page 2 is not "the next
 * twenty newest", and the filters and unread count would be wrong over one server page.
 */
const NOTIFICATIONS_PER_PAGE = 20;

/**
 * Filters and page live in the URL, like the Packages list: a refresh, the back button or a shared
 * link restores the same view. Anything unrecognised falls back to "all", and a page past the end
 * is clamped to the last one that exists.
 */
const PARAM = { type: 'type', status: 'status', page: 'page' } as const;
const KNOWN_TYPES = new Set<string>(NOTIFICATION_TYPES.map((entry) => entry.code));

function parseTypeFilter(params: URLSearchParams): string {
  const value = params.get(PARAM.type);
  return value !== null && KNOWN_TYPES.has(value) ? value : ALL_TYPES;
}

function parseStatusFilter(params: URLSearchParams): StatusFilter {
  return params.get(PARAM.status) === STATUS.unread ? STATUS.unread : STATUS.all;
}

function FilterField({
  label,
  children,
}: {
  label: string;
  /** The dropdown, given the id that ties the label to it. */
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className="text-xs font-semibold tracking-wide text-muted uppercase">
        {label}
      </label>
      {children(id)}
    </div>
  );
}

/**
 * Full notification list — the destination for the Dashboard panel's rows and the header bell.
 * Type and status filters mirror the old system's 站内信息 page, but filter live instead of behind
 * a 查找 button. Selecting a row opens it in a side panel and marks it read.
 *
 * Real data as of MYP-112: four states, not one (the same ladder Orders/Warehouses already use)
 * — loading and failed are about the request, `notifications.empty` and `.noMatches` are about
 * the answer once it's in.
 *
 * Rows are keyed by `notificationKey`, not a wire `id` — `get_msgs` sends no `id` field at all.
 */
export function Notifications() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { notifications, unreadCount, status, markRead, markAllRead, reload } = useNotifications();
  // Passed to resolveNotificationLink, which no longer reaches packages on its own (MYP-40).
  const { packages } = usePackages();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = parseTypeFilter(searchParams);
  const statusFilter = parseStatusFilter(searchParams);
  const [openId, setOpenId] = useState<string | null>(null);

  /** Sets one filter and goes back to page 1; the default value is dropped from the URL. */
  const setFilter = (name: 'type' | 'status', value: string, fallback: string) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value === fallback) params.delete(PARAM[name]);
      else params.set(PARAM[name], value);
      params.delete(PARAM.page);
      return params;
    });

  /**
   * Same URL handling as the Packages list, plus a scroll back to the top of the page: the pager
   * sits under twenty rows, so without it the next page would open at its bottom. The page top
   * rather than the list's own top, because the header is sticky and would cover the filters.
   */
  const goToPage = (next: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next <= 1) params.delete(PARAM.page);
      else params.set(PARAM.page, String(next));
      return params;
    });
    window.scrollTo({ top: 0 });
  };

  const filtered = notifications.filter((item) => {
    if (typeFilter !== ALL_TYPES && item.type !== typeFilter) return false;
    // The row you just opened stays put while filtering by unread — marking it read on open
    // would otherwise make it vanish from under the cursor.
    if (
      statusFilter === STATUS.unread &&
      item.readflag === 'Y' &&
      notificationKey(item) !== openId
    ) {
      return false;
    }
    return true;
  });

  const pageCount = packagePageCount(filtered.length, NOTIFICATIONS_PER_PAGE);
  const page = clampPackagePage(
    parsePackagePage(searchParams),
    filtered.length,
    NOTIFICATIONS_PER_PAGE,
  );
  const pageStart = (page - 1) * NOTIFICATIONS_PER_PAGE;
  const visible = filtered.slice(pageStart, pageStart + NOTIFICATIONS_PER_PAGE);

  /**
   * Most categories have a page where the user acts on it, and those rows are links that navigate
   * on their own. PACKAGE and ORDER are resolved per-notification instead (`resolveNotificationLink`)
   * — most resolve straight to their own thread or order, but one whose subject can't be found
   * still has nowhere to go, so it opens in place here exactly like every notification used to
   * before MYP-67/MYP-112.
   */
  const selectNotification = (key: string, opensInPlace: boolean) => {
    markRead(key);
    if (opensInPlace) setOpenId(key);
  };

  const opened = notifications.find((item) => notificationKey(item) === openId) ?? null;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-semibold text-ink">{t('notifications.title')}</h1>
        <p className="text-sm text-muted">
          {t('notifications.unread', { total: fmt.number(unreadCount) })}
        </p>
        {status === 'ready' && unreadCount > 0 ? (
          <button
            type="button"
            onClick={markAllRead}
            className="ml-auto inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand hover:underline lg:min-h-0"
          >
            <Icon name="done_all" size={18} />
            {t('notifications.markAllRead')}
          </button>
        ) : null}
      </div>

      <div className="mb-4 flex flex-wrap gap-4">
        <FilterField label={t('notifications.filters.type')}>
          {(id) => (
            <Select
              id={id}
              value={typeFilter}
              onChange={(next) => setFilter('type', next, ALL_TYPES)}
              options={[
                { value: ALL_TYPES, label: t('notifications.filters.allTypes') },
                ...NOTIFICATION_TYPES.map((entry) => ({
                  value: entry.code,
                  label: t(entry.labelKey),
                })),
              ]}
              className="sm:w-56"
            />
          )}
        </FilterField>

        <FilterField label={t('notifications.filters.status')}>
          {(id) => (
            <Select
              id={id}
              value={statusFilter}
              onChange={(next) => setFilter('status', next, STATUS.all)}
              options={[
                { value: STATUS.all, label: t('notifications.filters.allStatuses') },
                { value: STATUS.unread, label: t('notifications.filters.unreadOnly') },
              ]}
              className="sm:w-56"
            />
          )}
        </FilterField>
      </div>

      {status === 'loading' ? (
        <EmptyState icon="hourglass_empty" title={t('notifications.loading')} />
      ) : status === 'error' ? (
        <div className="grid justify-items-center gap-4 py-4">
          <EmptyState
            icon="cloud_off"
            title={t('notifications.failed')}
            description={t('notifications.failedBody')}
          />
          <button type="button" onClick={reload} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('actions.retry')}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="notifications"
          title={
            notifications.length === 0 ? t('notifications.empty') : t('notifications.noMatches')
          }
        />
      ) : (
        <>
          <Card className="divide-y divide-line p-0">
            {visible.map((notification) => {
              const key = notificationKey(notification);
              const href = resolveNotificationLink(notification, packages);
              const opensInPlace = href === null;

              return (
                <NotificationRow
                  key={key}
                  notification={notification}
                  href={href ?? undefined}
                  onSelect={() => selectNotification(key, opensInPlace)}
                />
              );
            })}
          </Card>

          <Pager
            page={page}
            pageCount={pageCount}
            rangeStart={pageStart + 1}
            rangeEnd={pageStart + visible.length}
            total={filtered.length}
            onGo={goToPage}
            label={t('notifications.pagerLabel')}
          />
        </>
      )}

      {opened ? (
        <NotificationThreadPanel notification={opened} onClose={() => setOpenId(null)} />
      ) : null}
    </>
  );
}
